import { Link } from "react-router-dom";
import { useHandleCourses } from "../../../core/hooks/useHandleCourses";
import { useAuth } from "../../../core/hooks/useAuth";
import { all_routes } from "../../router/all_routes";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { convertTimestampToDate } from "../../../core/common/ults/dateTime";
import { Progress } from "antd";
import { useTrackList } from "../../../core/hooks/useTrackVideo";
import { extractTextAndUrls } from "../../../core/common/ults/domParser";
import {
    buildMediaStatusMap,
    lookupMediaProgress,
} from "../../../core/common/ults/trackProgress";

const ProgressCard = ({ courseData }: any) => {
    const route = all_routes;
    const { moodleUser } = useAuth();
    const { courseContents } = useHandleCourses(courseData?.id);
    const { data: trackList } = useTrackList(courseData?.id, moodleUser);
    const { t, i18n } = useTranslation();

    // --- Tính Lý thuyết ---
    const videoStatusMap = useMemo(
        () => buildMediaStatusMap(trackList),
        [trackList]
    );

    const getVideoInfo = (url: string) => {
        const entry = lookupMediaProgress(videoStatusMap, url);
        if (!entry) return { status: t("progress.notWatched"), percent: 0 };
        const label =
            entry.status === "completed"
                ? t("progress.watched")
                : entry.status === "inProgress"
                ? t("progress.watching")
                : t("progress.notWatched");
        return { status: label, percent: entry.percent };
    };

    const weekData = (courseContents || [])
        .filter((section: any) => section.section > 0)
        .map((section: any) => {
            const videos = section.modules?.flatMap((mod: any) => {
                const list: any[] = [];
                const pushVideo = (name: string, url: string) => {
                    const info = getVideoInfo(url);
                    list.push({ name, ...info });
                };
                if (mod.modname === "unilabel") {
                    const result = extractTextAndUrls(mod.description);
                    result?.items?.forEach((res: any) => {
                        const url = (res.url || "").toLowerCase().split("?")[0];
                        if (
                            res.type === "video" ||
                            res.type === "audio" ||
                            res.type === "pdf" ||
                            url.endsWith(".mp4") ||
                            url.endsWith(".mp3") ||
                            url.endsWith(".wav") ||
                            url.endsWith(".m4a") ||
                            url.endsWith(".aac") ||
                            url.endsWith(".pdf")
                        ) {
                            pushVideo(res.text, res.url);
                        }
                    });
                }
                return list;
            }) || [];
            return { videos };
        });

    const calculateWeekProgress = (videos: any[]) => {
        if (!videos || videos.length === 0) return 0;

        const total = videos.length;
        const completed = videos.filter((v: any) => v.status === t("progress.watched")).length;
        return Math.round((completed / total) * 100);
    };



    const totalWeeks = weekData?.filter((week: any) => week.videos.length > 0).length;
    const completedWeeks = weekData.filter(
        (week: any) => calculateWeekProgress(week.videos) === 100
    ).length;


    const theoryProgressPercent = totalWeeks > 0
        ? Math.round((completedWeeks / totalWeeks) * 100)
        : 0;


    // --- Tính Bài tập & Hoạt động khác ---
    const allModules = courseContents?.flatMap((section: any) => section.modules || []) || [];
    const quizModules = allModules.filter((m: any) => m.modname === "quiz");
    const assignModules = allModules.filter((m: any) => m.modname === "assign");

    const completedQuiz = quizModules.filter((m: any) => [1, 2].includes(m.completiondata?.state)).length;
    const completedAssign = assignModules.filter((m: any) => [1, 2].includes(m.completiondata?.state)).length;

    const exerciseProgressPercent = (quizModules.length + assignModules.length)
        ? Math.round(((completedQuiz + completedAssign) / (quizModules.length + assignModules.length)) * 100)
        : 0;

    const otherModules = allModules.filter((m: any) => m.modname === "forum" && m.completion > 0);
    const completedOtherModules = otherModules.filter((m: any) => [1, 2].includes(m.completiondata?.state));
    const otherProgressPercent = otherModules.length
        ? Math.round((completedOtherModules.length / otherModules.length) * 100)
        : 0;

    // --- Render ---
    const startDate = useMemo(() => convertTimestampToDate(courseData?.startdate), [courseData?.startdate, i18n.language]);
    const endDate = useMemo(() => convertTimestampToDate(courseData?.enddate), [courseData?.enddate, i18n.language]);

    return (
        <Link to={`${route.studentProgressDetail}/${courseData.id}`} className="d-flex flex-column flex-md-row align-items-center justify-content-between border p-3 mb-3 rounded-2 forum-card hover-effect">
            <div className="mb-3 mb-md-0">
                <h6 className="mb-2 forum-title">{courseData?.fullname}</h6>
                <p className="mb-2 forum-intro"><i className="isax isax-timer-start me-1" /> {t("progress.startDate")}: {startDate}</p>
                <p className="mb-2 forum-intro"><i className="isax isax-timer-start me-1" /> {t("progress.endDate")}: {endDate}</p>
            </div>
            <div className="d-flex gap-4 gap-md-5">
                {[
                    { title: t("progress.theory"), percent: theoryProgressPercent },
                    { title: t("progress.exercises"), percent: exerciseProgressPercent },
                    { title: t("progress.activity"), percent: otherProgressPercent },
                ].map((item, idx) => (
                    <div key={idx} className="d-flex flex-column align-items-center">
                        <p className="mb-2 text-center">{item.title}</p>
                        <Progress
                            type="circle"
                            percent={item.percent}
                            strokeColor={item.percent === 100 ? "#52c41a" : { "0%": "#ff4d4f", "40%": "#faad14", "90%": "#52c41a" }}
                            size={90}
                        />
                    </div>
                ))}
            </div>
        </Link>
    );
};

export default ProgressCard;